import { Component, Fragment } from 'react';
import { concat, from } from 'rxjs';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { ScriptLoadService } from '../../../services/script-load.service';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { Lines, pageCopy, RichText } from '../page-copy/page-copy';
import styles from './product-page.component.module.scss';

export default class ProductPageComponent extends Component<any> {

    @lazyInject(ScriptLoadService) private scriptLoader: ScriptLoadService;

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        concat(
            from(this.scriptLoader.loadAllScriptsIfNotLoaded(true)),
            from(this.scriptLoader.loadStripe()),
            from(ScriptLoadService.loadPayPal()),
        ).pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                // skip
            }
        });
        // TODO Preact fix
        // this.renderer.addClass(this.document.body, 'product-page');
    }

    componentWillUnmount() {
        // TODO Preact fix
        // this.renderer.removeClass(this.document.body, 'product-page');
    }

    render() {
        // Page copy = SSR page data (xconvert-web-ssr sources/page-data-overrides/product.json). The plan cards
        // (prices, limits, buttons) and the hero badge stay here: plan data / UI, not page copy.
        const { seo, content: c } = pageCopy(this.props);
        const features = c.features || {};
        const faq = c.faq || {};
        return <div className="container-fluid p-0"> {/* Remove default padding */}
            <div className={`${styles['product-page']}`}>
                <div className={styles['hero']}>
                    <div className={styles['hero-badge']}>UNLIMITED</div>
                    <h1 className={styles['hero-title']}>
                        <Lines text={c.h1 || seo.title} last={line => <span className={styles['hero-gradient']}>{line}</span>} />
                    </h1>
                    <p className={styles['hero-subtitle']}>{seo.description}</p>
                </div>

                {/* Hero Illustration */}
                <div className={`container ${styles['content-section']}`}>
                    <div className={styles['display-image']}>
                        <img className={styles['frame']} src="/img/xconvert/hero-conversion.svg" alt="File conversion illustration - Convert, compress and merge files" />
                    </div>
                </div>


                {/* Features Section */}
                <div className={`container mt-5 pt-5 ${styles["content-section"]}`}>
                    <div className="text-center mb-5">
                        <div className={styles['section-icon']}>{features.icon}</div>
                        <h2 className={styles['section-title']}>{features.title}</h2>
                        <p className={styles['section-subtitle']}>{features.subtitle}</p>
                    </div>

                    <div className="row">
                        {(features.cards || []).map((card: any, i: number) => <div key={i} className="col-md-6 mb-4">
                            <div className={styles['feature-card']}>
                                <div className={styles['feature-icon']}>{card.icon}</div>
                                <h4 className={styles['feature-title']}>{card.title}</h4>
                                <p className={styles['feature-text']}>{card.text}</p>
                            </div>
                        </div>)}
                    </div>
                </div>

                {/* Pricing Section */}
                <div className={`container mt-5 pt-4 ${styles["content-section"]}`}>
                    <div className="text-center mb-4">
                        <h2 className={styles['section-title']}>{c.pricing?.title}</h2>
                        <RichText className={styles['section-subtitle']} html={c.pricing?.subtitleHtml} />
                    </div>
                    <div className={styles['packages']}>
                        <div className={styles['package-container']}>
                            <div className={styles['package']}>
                                <h4>Basic</h4>
                                <div className={styles['pricing-display']}>
                                    <span className={styles['price-large']}>$4.99</span>
                                    <span className={styles['price-sub']}>per month</span>
                                    <span className={styles['price-annual']}>or $49.99 billed yearly</span>
                                </div>
                                <ul>
                                    <li className={styles['check']}><b>No ads — nothing between you and your file</b></li>
                                    <li className={styles['check']}>1 GB file size limit</li>
                                    <li className={styles['check']}>4 hr/task process time</li>
                                    <li className={styles['check']}>10 Concurrent non-video tasks</li>
                                    <li className={styles['check']}>4 Concurrent video tasks</li>
                                    <li className={styles['check']}>200 video conversions per day</li>
                                    <li className={styles['check']}>8 hours of processing time per day**</li>
                                    <li className={styles['check']}>Advanced encoders</li>
                                </ul>
                                <a className={`${styles['btn']} ${styles['btn-outline']}`} href="/product/backer_monthly">
                                    Start Basic
                                </a>
                            </div>
                        </div>
                        <div className={styles['package-container']}>
                            <div className={`${styles['package']} ${styles['has-badge']}`}>
                                <div className={styles['recommended-badge']}>Recommended</div>
                                <h4>Pro</h4>
                                <div className={styles['pricing-display']}>
                                    <span className={styles['price-large']}>$9.99</span>
                                    <span className={styles['price-sub']}>per month</span>
                                    <span className={styles['price-annual']}>or $99.99 billed yearly</span>
                                </div>
                                <ul>
                                    <li className={styles['check']}><b>No ads — nothing between you and your file</b></li>
                                    <li className={styles['check']}>4 GB file size limit</li>
                                    <li className={styles['check']}>Fastest file upload*</li>
                                    <li className={styles['check']}>4 hr/task process time</li>
                                    <li className={styles['check']}>25 Concurrent non-video tasks</li>
                                    <li className={styles['check']}>8 Concurrent video tasks</li>
                                    <li className={styles['check']}>400 video conversions per day</li>
                                    <li className={styles['check']}>24 hours of processing time per day**</li>
                                    <li className={styles['check']}>High compression encoders</li>
                                </ul>
                                <div className={styles['footnote']}>
                                    * Files upload in parallel chunks to the nearest edge server in our global network for the fastest available transfer speeds.
                                    <br />** Processing time is the CPU time our servers spend on your files — a typical video conversion uses 1–5 minutes. Daily allowances reset at midnight UTC.
                                </div>
                                <a className={`${styles['btn']} ${styles['btn-primary']}`} href="/product/patron_monthly">
                                    Start Pro
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className={`container mt-5 pt-5 pb-5 ${styles["content-section"]}`}>
                    <div className="text-center mb-5">
                        <h2 className={styles['section-title']}>{faq.title}</h2>
                    </div>

                    <div className="row justify-content-center">
                        {(faq.groups || []).map((group: any, g: number) =>
                            <div key={g} className={g === 0 ? 'col-md-5 mr-md-4 mb-4' : 'col-md-5'}>
                                <h5 className={styles['qa-section']}>{group.title}</h5>
                                {(group.items || []).map((item: any, i: number) => <Fragment key={i}>
                                    <h6 className={styles['qa-question']}>{item.question}</h6>
                                    <RichText className={styles['qa-answer']} html={item.answerHtml} />
                                </Fragment>)}
                            </div>)}
                    </div>
                </div>
            </div>
        </div>
    }
}
